import api from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type PreboardingInvitationStatus = 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'REVOKED';

/**
 * The HR-facing invitation row. Mirrors the backend `INVITATION_SELECT` shape in
 * `preboarding-invitations.service.ts` — note the raw token is never exposed; the
 * secure link lives only in the emailed message.
 */
export interface PreboardingInvitation {
  id: string;
  formId: string;
  employeeId: string;
  recipientEmail: string;
  status: PreboardingInvitationStatus;
  expiresAt: string;
  sentAt: string;
  completedAt: string | null;
  revokedAt: string | null;
}

export const preboardingInvitationKeys = {
  all: ['preboarding-invitations'] as const,
  list: (formId: string) => [...preboardingInvitationKeys.all, formId] as const,
};

async function listInvitations(formId: string): Promise<PreboardingInvitation[]> {
  const { data } = await api.get<PreboardingInvitation[]>(
    `/preboarding-forms/${formId}/invitations`,
  );
  return data;
}

async function sendInvitation(
  formId: string,
  employeeId: string,
): Promise<PreboardingInvitation> {
  const { data } = await api.post<PreboardingInvitation>(
    `/preboarding-forms/${formId}/invitations`,
    { employeeId },
  );
  return data;
}

async function resendInvitation(
  formId: string,
  employeeId: string,
): Promise<PreboardingInvitation> {
  const { data } = await api.post<PreboardingInvitation>(
    `/preboarding-forms/${formId}/invitations/resend`,
    { employeeId },
  );
  return data;
}

export function usePreboardingInvitations(formId: string, enabled = true) {
  return useQuery({
    queryKey: preboardingInvitationKeys.list(formId),
    queryFn: () => listInvitations(formId),
    enabled: !!formId && enabled,
  });
}

export function useSendPreboardingInvitation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: string) => sendInvitation(formId, employeeId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preboardingInvitationKeys.list(formId) });
    },
  });
}

export function useResendPreboardingInvitation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: string) => resendInvitation(formId, employeeId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preboardingInvitationKeys.list(formId) });
    },
  });
}
